// _forms/PCFormulario.jsx
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useFormulario from '../_shared/hooks/useFormulario';
import ConditionalModal from '../_shared/components/ConditionalModal';
import { PaymentModal } from '../../../payment';
import { formatters } from '../_shared/utils/formatters';
import { validators } from '../_shared/utils/validators';
import DateSelectors from '../_shared/components/DateSelectors';
import BannerRenderer from '../_shared/components/BannerRenderer';
import './PCFormulario.css';

const PCFormulario = ({
  isEditable = false,
  selectedPackage = null,
  isFillingForm = true,
  onTabChange,
}) => {
  const { slug } = useParams();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const {
    formConfig,
    setFormConfig,
    paquetes,
    selectedPaquete,
    selectedAddons,
    setSelectedAddons,
    aceptaDeslinde,
    setAceptaDeslinde,
    deletedFields,
    error,
    isLoading,
    resumenModal,
    modalOpen,
    activeModal,
    selectedField,
    existingConditions,
    conditions,
    optionConditionModal,
    setModalOpen,
    setActiveModal,
    setOptionConditionModal,
    setConditions,
    shouldShowOption,
    shouldRenderField,
    isFormValid,
    handlePaqueteChange,
    handleCloseModal,
    handleOpenModal,
    handleSaveConditions,
    handleFieldChangeWrapper,
    handleOptionConditionsWrapper,
    totalAmount,
    // 🟢 NUEVO: funciones para filtrar reglas correctamente
    calculateAge,
    getAutoCategoryRule,
    getVisibleCategoryRules,
  } = useFormulario({
    slug,
    baseUrl,
    selectedPackage,
    isEditable,
    isFillingForm,
    onTabChange,
  });

  // ==========================================
  // 1. SECCIÓN DE MONTO
  // ==========================================
  const renderAmountSection = () => {
    if (!selectedPaquete) return null;
    
    // 🔥 BÚSQUEDA ROBUSTA: Verifica ambas propiedades
    const paquete = paquetes.find((p) => 
      String(p.id_package) === String(selectedPaquete) || 
      String(p.id) === String(selectedPaquete)
    );
    
    if (!paquete) {
      console.warn("⚠️ No se encontró el paquete con ID:", selectedPaquete);
      return null;
    }
    
    const amtPerPerson = parseFloat(paquete.amount) || 0;
    const numCompetitors = parseInt(paquete.number_of_competitors) || 1;
    const addons = paquete.addons || [];

    const renderAddonControl = (addon) => {
      const maxQty = numCompetitors;
      const currentQty = parseInt(selectedAddons[addon.id]) || 0;
      if (numCompetitors === 1) {
        return (
          <input
            type="checkbox"
            checked={currentQty > 0}
            onChange={(e) => {
              setSelectedAddons((prev) => ({
                ...prev,
                [addon.id]: e.target.checked ? 1 : 0,
              }));
            }}
            className="pc-addon-checkbox"
          />
        );
      }
      return (
        <div className="pc-addon-quantity">
          <input
            type="number"
            min="0"
            max={maxQty}
            step="1"
            value={currentQty}
            onChange={(e) => {
              let val = parseInt(e.target.value, 10);
              if (isNaN(val)) val = 0;
              if (val > maxQty) val = maxQty;
              if (val < 0) val = 0;
              setSelectedAddons((prev) => ({
                ...prev,
                [addon.id]: val,
              }));
            }}
            className="pc-addon-number"
          />
          <span className="pc-addon-max">Máx: {maxQty}</span>
        </div>
      );
    };

    return (
      <div className="pc-form-field pc-package-section">
        <label className="pc-amount-label">📦 Información del Paquete</label>
        <div className="pc-package-details">
          <div className="pc-package-detail">
            <span className="pc-detail-label">Por persona:</span>
            <span className="pc-detail-value">
              {formatters.formatCurrency(amtPerPerson, paquete.currency)}
            </span>
          </div>
          <div className="pc-package-detail">
            <span className="pc-detail-label">Competidores:</span>
            <span className="pc-detail-value pc-competitor-badge">
              {numCompetitors}{' '}
              {numCompetitors === 1
                ? '(Individual)'
                : `(Equipo de ${numCompetitors})`}
            </span>
          </div>
          {addons.length > 0 && (
            <div className="pc-package-detail pc-addons-section">
              <span className="pc-detail-label">Opciones adicionales:</span>
              <div className="pc-addons-list">
                {addons.map((addon) => {
                  const qty = parseInt(selectedAddons[addon.id]) || 0;
                  const subtotal = (
                    parseFloat(addon.extra_amount || 0) * qty
                  ).toFixed(2);
                  return (
                    <div key={addon.id} className="pc-addon-item">
                      {renderAddonControl(addon)}
                      <span className="pc-addon-label">{addon.label}</span>
                      <span className="pc-addon-price">
                        +{formatters.formatCurrency(subtotal, paquete.currency)}
                        {qty > 1 && (
                          <span className="pc-addon-qty-badge">x{qty}</span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="pc-package-detail pc-total-amount">
            <span className="pc-detail-label pc-total-label">
              💰 Total del paquete:
            </span>
            <span className="pc-detail-value pc-total-value">
              {formatters.formatCurrency(totalAmount, paquete.currency)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // 1.5 VALIDACIÓN POR CAMPO
  // ==========================================
  const validateFieldOnBlur = (field) => {
    const fieldErrors = validators.validateFieldExtended(field, field.value);
    if (field.type === 'date') {
      if (field.required && (!field.value || String(field.value).trim() === '')) {
        if (!fieldErrors.includes('Este campo es obligatorio.')) {
          fieldErrors.unshift('Debes seleccionar una fecha completa.');
        }
      }
      if (field.value && String(field.value).trim() !== '') {
        const date = new Date(field.value);
        if (isNaN(date.getTime())) {
          if (!fieldErrors.some((e) => e.includes('inválid'))) {
            fieldErrors.push('La fecha seleccionada no es válida.');
          }
        }
      }
    }
    setFormConfig((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        fields: prev.fields.fields.map((f) =>
          f.id === field.id
            ? {
                ...f,
                invalid: fieldErrors.length > 0,
                errorMessage: fieldErrors.length > 0 ? fieldErrors[0] : '',
              }
            : f
        ),
      },
    }));
  };

  // ==========================================
  // 🔍 FUNCIÓN PARA OBTENER EDAD DESDE LA FECHA REAL
  // ==========================================
  const getComputedAge = useCallback((fields) => {
    const dateField = fields.find((f) => {
      const esFechaPorTipo = f.type === 'date';
      const esFechaPorLabel =
        f.label?.toLowerCase().includes('fecha') ||
        f.label?.toLowerCase().includes('nacimiento') ||
        f.label?.toLowerCase().includes('cumpleaños');
      const esFechaPorDataType = f.dataType === 'date';
      return esFechaPorTipo || esFechaPorLabel || esFechaPorDataType;
    });
    if (!dateField?.value) return null;
    return calculateAge(dateField.value);
  }, [calculateAge]);

  // ==========================================
  // 2. RENDERIZADO DE CAMPOS
  // ==========================================
  const renderFormFields = () => {
    if (!formConfig?.fields?.fields || !Array.isArray(formConfig.fields.fields)) {
      return null;
    }
    return formConfig.fields.fields
      .filter(
        (field) => !field.isPackageInclude && !deletedFields.includes(field.id)
      )
      .map((field, index) => {
        if (!isEditable && !shouldRenderField(field)) {
          return null;
        }
        const fieldValue =
          field.value !== undefined && field.value !== null ? field.value : '';
        const isInvalid = field.invalid ? 'pc-invalid-field' : '';

        return (
          <div
            key={field.id || index}
            className={`pc-form-field ${isInvalid}`}
            id={`field-${field.id}`}
          >
            {field.type === 'banner' ? (
              <div
                className="pc-banner-field"
                style={{
                  backgroundColor: '#eef6ff',
                  borderLeft: '4px solid #1890ff',
                  padding: '14px 18px',
                  borderRadius: '6px',
                  color: '#1f2937',
                  fontSize: '0.98rem',
                  lineHeight: '1.5',
                  margin: '8px 0',
                }}
              >
                <div
                  style={{
                    fontWeight: 'bold',
                    marginBottom: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '1.05rem',
                    color: '#096dd9',
                  }}
                >
                  <span>📢</span> {field.label}
                </div>
                <BannerRenderer content={field.value} />
              </div>
            ) : (
              <>
                <label className="pc-field-label">
                  {field.label}
                  {field.required && <span className="pc-required-asterisk"> *</span>}
                  {field.invalid && <span className="pc-error-icon"> ⚠️</span>}
                </label>

                {/* ✅ Campo Fecha */}
                {field.type === 'date' && (
                  <div className="pc-field-wrapper">
                    <DateSelectors
                      value={field.value || ''}
                      onChange={(dateStr) => {
                        const event = {
                          target: {
                            value: dateStr,
                            name: field.label,
                          },
                        };
                        handleFieldChangeWrapper(
                          event,
                          field.id,
                          setFormConfig,
                          isFillingForm
                        );
                      }}
                      required={field.required}
                      disabled={!isEditable && !isFillingForm}
                      className={field.invalid ? 'error' : ''}
                    />
                  </div>
                )}

                {/* ✅ Campo Categoría con FIX #2 y #3 */} 
                {field.type === 'category' && (
                  <div className="pc-category-field">
                    <select
                    className={`pc-field-select ${field.invalid ? 'pc-invalid-select' : ''}`}
  value={fieldValue}
  onChange={(e) => {
    if (field.isUserSelected) {
      setFormConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          fields: prev.fields.fields.map((f) =>
            f.id === field.id ? { ...f, value: e.target.value } : f
          ),
        },
      }));
    }
  }}
  disabled={!field.isUserSelected}
>
  <option value="">
    {field.isUserSelected
      ? 'Selecciona una categoría'
      : 'Categoría asignada automáticamente'}
  </option>
  {getVisibleCategoryRules(
    field.categoryRules || [],
    getComputedAge(formConfig.fields.fields),
    field.isUserSelected  // ← clave: si es true, muestra TODAS
  ).map((rule, rIdx) => (
    <option key={rIdx} value={rule.label}>
      {rule.label}
      {!rule.isSpecial && ` (${rule.minAge}-${rule.maxAge} años)`}
    </option>
  ))}
</select>

                    {/* ✅ Checkbox para categoría especial — FIX #2 */}
                    {/* ✅ Checkbox para cambiar de categoría — SIEMPRE visible para campos category */}
{field.type === 'category' && (
  <label className="pc-special-category-toggle">
    <input
      type="checkbox"
      checked={field.isUserSelected || false}
      onChange={(e) => {
        const isChecked = e.target.checked;
        setFormConfig((prev) => {
          const computedAge = getComputedAge(prev.fields.fields);
          const currentRules = field.categoryRules || [];

          let autoValue = field.value;
          if (!isChecked && computedAge !== null) {
            const rule = getAutoCategoryRule(currentRules, computedAge);
            autoValue = rule ? rule.label : '';
          }

          return {
            ...prev,
            fields: {
              ...prev.fields,
              fields: prev.fields.fields.map((f) =>
                f.id === field.id
                  ? {
                      ...f,
                      isUserSelected: isChecked,
                      value: autoValue,
                    }
                  : f
              ),
            },
          };
        });
      }}
      className="pc-special-checkbox"
    />
    <span className="pc-special-text">
      Quiero cambiar de categoría
    </span>
  </label>
)}
                    <small className="pc-help-text">
                      💡 Categoría sugerida automáticamente según tu edad y paquete. Si calificas para una categoría especial o quieres elegir otra categoría, marca la casilla.
                      <strong style={{ color: '#d97706', display: 'block', marginTop: '4px' }}>
                        ⚠️ Por favor, revisa bien tu categoría seleccionada: una vez iniciada la competencia no se realizarán cambios ni reclamos.
                      </strong>
                    </small>
                  </div>
                )}

                {/* ✅ Campo Edad bloqueado */}
                {field.type === 'text' &&
                  field.dataType === 'integer' &&
                  (field.label?.toLowerCase().includes('edad') ||
                    field.label?.toLowerCase() === 'edad') && (
                    <div className="pc-age-field">
                      <input
                        type="text"
                        value={field.value || ''}
                        readOnly
                        disabled
                        className={`pc-field-input pc-locked-field`}
                        placeholder="Se calcula automáticamente"
                      />
                      <small className="pc-help-text">
                        🔒 Tu edad se calcula automáticamente desde tu fecha de
                        nacimiento
                      </small>
                    </div>
                  )}

                {/* Campos normales (sin cambios) */}
                {field.type === 'text' &&
                  field.dataType === 'integer' &&
                  !(
                    field.label?.toLowerCase().includes('edad') ||
                    field.label?.toLowerCase() === 'edad'
                  ) && (
                    <input
                      type="number"
                      name={field.label}
                      value={fieldValue}
                      onChange={(e) =>
                        handleFieldChangeWrapper(
                          e,
                          field.id,
                          setFormConfig,
                          isFillingForm
                        )
                      }
                      onBlur={() => validateFieldOnBlur(field)}
                      required={field.required}
                      className={`pc-field-input ${
                        field.invalid ? 'pc-invalid-input' : ''
                      }`}
                      placeholder={`Ingresa ${field.label.toLowerCase()}`}
                    />
                  )}

                {field.type === 'text' && field.dataType === 'email' && (
                  <input
                    type="email"
                    name={field.label}
                    value={fieldValue}
                    onChange={(e) =>
                      handleFieldChangeWrapper(
                        e,
                        field.id,
                        setFormConfig,
                        isFillingForm
                      )
                    }
                    onBlur={() => validateFieldOnBlur(field)}
                    required={field.required}
                    className={`pc-field-input ${
                      field.invalid ? 'pc-invalid-input' : ''
                    }`}
                    placeholder="ejemplo@correo.com"
                  />
                )}

                {field.type === 'text' &&
                  !['integer', 'email'].includes(field.dataType) &&
                  !(
                    field.label?.toLowerCase().includes('edad') ||
                    field.label?.toLowerCase() === 'edad'
                  ) && (
                    <input
                      type="text"
                      name={field.label}
                      value={fieldValue}
                      onChange={(e) =>
                        handleFieldChangeWrapper(
                          e,
                          field.id,
                          setFormConfig,
                          isFillingForm
                        )
                      }
                      onBlur={() => validateFieldOnBlur(field)}
                      required={field.required}
                      className={`pc-field-input ${
                        field.invalid ? 'pc-invalid-input' : ''
                      }`}
                      placeholder={`Ingresa ${field.label.toLowerCase()}`}
                    />
                  )}

                {field.type === 'select' && (
                  <select
                    name={field.label}
                    value={fieldValue}
                    onChange={(e) =>
                      handleFieldChangeWrapper(
                        e,
                        field.id,
                        setFormConfig,
                        isFillingForm
                      )
                    }
                    onBlur={() => validateFieldOnBlur(field)}
                    required={field.required}
                    className={`pc-field-select ${
                      field.invalid ? 'pc-invalid-select' : ''
                    }`}
                  >
                    <option value="">Seleccione una opción</option>
                    {field.options
                      .filter((opt) => isEditable || shouldShowOption(opt))
                      .map((opt, optionIndex) => {
                        const label = typeof opt === 'string' ? opt : opt.label;
                        return (
                          <option key={optionIndex} value={label}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                )}

                {field.invalid && (
                  <p className="pc-error-message">{field.errorMessage}</p>
                )}
              </>
            )}
          </div>
        );
      });
  };

  // ==========================================
  // 3. RENDER PRINCIPAL (sin cambios)
  // ==========================================
  if (isLoading)
    return <div className="pc-loading">⏳ Cargando formulario...</div>;
  if (error) return <div className="pc-error">❌ Error: {error}</div>;

  return (
    <div className="pc-formulario-container" id="pc-formulario">
      <h1 className="pc-formulario-titulo">📝 Formulario de Inscripción</h1>
      <p className="pc-formulario-subtitulo">
        Completa todos los campos para registrarte en el evento.
      </p>

      <div className="pc-paquete-container">
        <label htmlFor="pc-paquete" className="pc-paquete-label">
          🎯 Seleccionar Paquete
        </label>
        <select
          id="pc-paquete"
          className="pc-paquete-select"
          value={selectedPaquete || ''}
          onChange={handlePaqueteChange}
        >
          <option value="">Seleccione un paquete</option>
          {paquetes.map((paquete) => {
            // 🔥 Usar id_package si existe, si no, usar id
            const packageValue = paquete.id_package || paquete.id;
            return (
              <option 
                key={packageValue} 
                value={packageValue}
              >
                {paquete.title || paquete.name}
              </option>
            );
          })}
        </select>
      </div>

      {renderAmountSection()}
      <div className="pc-form-fields">{renderFormFields()}</div>

      <div className="pc-deslinde-container">
        <h3 className="pc-deslinde-titulo">📋 Deslinde de Responsabilidades</h3>
        <div className="pc-deslinde-texto">
          <p>
            Al marcar esta casilla, declaro que he sido informado(a) y acepto los
            términos y condiciones del evento. Entiendo que la organización no se
            hace responsable por daños, pérdidas o accidentes que pudieran ocurrir
            durante la participación en el evento.
          </p>
          <p>
            Confirmo que los datos proporcionados son verídicos y autorizo su
            tratamiento según las políticas de privacidad.
          </p>
        </div>
        <label className="pc-deslinde-checkbox">
          <input
            type="checkbox"
            checked={aceptaDeslinde}
            onChange={(e) => setAceptaDeslinde(e.target.checked)}
            className="pc-deslinde-input"
          />
          <span className="pc-deslinde-text">
            He leído y acepto el deslinde de responsabilidades{' '}
            <span className="pc-required-asterisk">*</span>
          </span>
        </label>
      </div>

      <div className="pc-submit-container">
        <button
          className={`pc-submit-btn ${
            !isFormValid() ? 'pc-submit-disabled' : ''
          }`}
          onClick={handleOpenModal}
          disabled={!isFormValid()}
        >
          {!isFormValid() ? '🔒 Complete todos los campos' : '✅ Enviar Inscripción'}
        </button>
      </div>

      {/* Modales (sin cambios) */}
      {optionConditionModal && (
        <ConditionalModal
          isOpen={true}
          onClose={() => setOptionConditionModal(null)}
          fieldId={optionConditionModal.fieldId}
          fields={formConfig.fields.fields}
          paquetes={paquetes || []}
          existingConditions={optionConditionModal.option.conditions || []}
          onSave={(payload) => {
            const newConditions = Array.isArray(payload)
              ? payload
              : payload.conditions || [];
            const operator = Array.isArray(payload)
              ? 'OR'
              : payload.operator || 'OR';
            handleOptionConditionsWrapper(
              optionConditionModal.fieldId,
              optionConditionModal.optionIndex,
              newConditions,
              operator,
              setFormConfig
            );
            setOptionConditionModal(null);
          }}
        />
      )}

      {activeModal === 'condicional' && (
        <ConditionalModal
          isOpen={true}
          onClose={handleCloseModal}
          fieldId={selectedField}
          fields={formConfig.fields.fields}
          conditions={conditions}
          setConditions={setConditions}
          paquetes={paquetes || []}
          onSave={handleSaveConditions}
          existingConditions={existingConditions}
        />
      )}

      {modalOpen && resumenModal && (
        <PaymentModal
          resumen={resumenModal}
          onClose={handleCloseModal}
          onSubmit={() => {
            handleCloseModal();
          }}
          onTabChange={onTabChange}
          slug={slug}
        />
      )}
    </div>
  );
};

export default PCFormulario;